import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Form, Button } from 'react-bootstrap';
import AdminDataTable from './AdminDataTable';
import Swal from 'sweetalert2';
import { FaEdit, FaTrash, FaPlus, FaSearch, FaStore } from 'react-icons/fa';
import Navigation from './Navigation';
import AdminPageHeader from './AdminPageHeader';
import { filterMenuModuleRecords } from './outletMenuModuleRegistry';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function OutletModules() {
  const [modules, setModules] = useState([]);
  const [moduleMap, setModuleMap] = useState({});
  const [outletMap, setOutletMap] = useState({});
  const [filteredModules, setFilteredModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  // Fetch outlet list and build a map of outlet IDs to names.
  // Fetch outlet list and build a map of outlet IDs to names.
const fetchOutletList = async (token) => {
  try {
      const response = await fetch(`${API_BASE_URL}outletlist`, {
          method: 'POST',
          headers: { 
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}` 
          },
          body: JSON.stringify({ limit: 100, start: 0 })
      });
      const data = await response.json();
      if (data.error === "0" && data.results) {
          const map = data.results.reduce((acc, outlet) => {
              acc[outlet.id] = outlet.outlet_name;  // Updated keys here
              return acc;
          }, {});
          setOutletMap(map);
      }
  } catch (err) {
      console.error('Failed to fetch outlet list:', err);
  }
};


  // Fetch module list and build a map of module IDs to names.
  const fetchModuleList = async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}modulelist`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ limit: 100, start: 0 })
      });
      const data = await response.json();
      if (data.error === "0" && data.results) {
        const map = filterMenuModuleRecords(data.results).reduce((acc, module) => {
          acc[module.ID] = module.Modulename;
          return acc;
        }, {});
        setModuleMap(map);
      }
    } catch (err) {
      console.error('Failed to fetch module list:', err);
    }
  };

  // Wrap fetchModules in useCallback so that it is stable across renders.
  const fetchModules = useCallback(async (token) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}outlet-modules`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.error === 0 && data.data) {
        const transformedData = data.data.map(item => ({
          ID: item.id,
          OutletID: item.id_outlet,
          OutletName: outletMap[item.id_outlet] || `ID: ${item.id_outlet}`,
          Modules: item.modules
            .map(moduleId => moduleMap[moduleId] || `ID: ${moduleId}`)
            .join(', '),
          CreatedAt: item.created_at,
          UpdatedAt: item.updated_at
        }));
        setModules(transformedData);
        setFilteredModules(transformedData);
      }
    } catch (err) {
      console.error('Failed to fetch outlet modules:', err);
    } finally {
      setLoading(false);
    }
  }, [moduleMap, outletMap]);

  // On mount, fetch outlet and module lists.
  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return;
    }
    fetchOutletList(token);
    fetchModuleList(token);
  }, [navigate]);

  // Re-fetch modules when the outlet and module maps are ready.
  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (token && Object.keys(outletMap).length > 0 && Object.keys(moduleMap).length > 0) {
      fetchModules(token);
    }
  }, [fetchModules, outletMap, moduleMap]);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
    const filtered = modules.filter(module =>
      module.OutletName.toLowerCase().includes(value.toLowerCase()) ||
      module.Modules.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredModules(filtered);
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        const token = localStorage.getItem('sessionToken');
        try {
          const response = await fetch(`${API_BASE_URL}outlet-module`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ id })
          });
          const data = await response.json();
          if (data.error === 0) {
            Swal.fire('Deleted!', 'Module has been deleted.', 'success');
            fetchModules(token);
          } else {
            Swal.fire('Error!', 'Failed to delete module.', 'error');
          }
        } catch (err) {
          console.error(err);
          Swal.fire('Error!', 'Failed to delete module.', 'error');
        }
      }
    });
  };

  const columns = [
    { name: 'ID', selector: row => row.ID, sortable: true, width: '80px' },
    { name: 'Outlet Name', selector: row => row.OutletName, sortable: true, grow: 2 },
    { name: 'Modules', selector: row => row.Modules, sortable: true, grow: 2 },
    { name: 'Created At', selector: row => row.CreatedAt, sortable: true, grow: 2 },
    { name: 'Updated At', selector: row => row.UpdatedAt, sortable: true, grow: 2 },
    {
      name: 'Actions',
      cell: row => (
        <div className="d-flex gap-2">
          <Button variant="outline-warning" size="sm" onClick={() => navigate(`/edit-outlet-module/${row.OutletID}`)}>
            <FaEdit /> Edit
          </Button>
          <Button variant="outline-danger" size="sm" onClick={() => handleDelete(row.ID)}>
            <FaTrash /> Delete
          </Button>
        </div>
      ),
      width: '200px'
    }
  ];

  return (
    <div>
      <Navigation />
      <Container fluid className="admin-page-shell mt-4 px-4">
        <AdminPageHeader
          icon={FaStore}
          eyebrow="Operational configuration"
          title="Outlet Module Assignment"
          description="Kelola penetapan modul pada setiap outlet agar konfigurasi operasional tetap konsisten dan terdokumentasi dengan baik."
          breadcrumbs={[
            { label: 'Dashboard', to: '/dashboardadmin' },
            { label: 'Outlet Module Assignment' },
          ]}
          actions={(
            <Button variant="primary" onClick={() => navigate('/add-outlet-module')}>
              <FaPlus /> Assign Module
            </Button>
          )}
          meta={(
            <span className="soft-stat">
              {filteredModules.length} assignments
            </span>
          )}
        />

        <div className="section-card table-card">
          <div className="table-toolbar">
            <span className="status-chip">{filteredModules.length} assignments</span>
            <div className="control-with-icon search-control">
              <span className="control-with-icon__icon">
                <FaSearch />
              </span>
              <Form.Control
                type="text"
                placeholder="Search by outlet or module"
                value={search}
                onChange={handleSearch}
              />
            </div>
          </div>

          <div className="card-body pt-0">
            <AdminDataTable
              columns={columns}
              data={filteredModules}
              pagination
              progressPending={loading}
              highlightOnHover
              pointerOnHover
              responsive
              striped
              emptyTitle="No outlet module assignments found"
              emptyDescription="Tambahkan penetapan modul outlet untuk mulai mengelola akses operasional."
            />
          </div>
        </div>
      </Container>
    </div>
  );
}

export default OutletModules;
